import React from "react";
import { styled, useTheme } from "@material-ui/styles";
import { gql, useQuery } from "@apollo/client";
import { injectIntl } from "react-intl";
import { navigate } from "@reach/router";
import { Card, Theme } from "@material-ui/core";
import DirectionsRunIcon from "@material-ui/icons/DirectionsRunRounded";
import { IStyledArguments } from "../../types/styled-arguments";
import Link from "../link";
import Button from "../button";
import messages from "./PreSeasonCard.intl";

export const TEAM_PRE_SEASON_SUMMARY = gql`
  query TeamPreSeasonSummary($teamId: ID!) {
    teamPreSeasonExercises(teamId: $teamId) {
      id
      title
      totalEntries
      completedEntries
    }
  }
`;

const CardWrapper = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  flexDirection: "column",
  display: "flex",
  height: "100%",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(16, 21, 26, 0.08)",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "16px",
  },
}));

const Header = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const Content = styled("div")(({ theme }: IStyledArguments) => ({
  background: "transparent",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: theme.spacing(3),
  gap: theme.spacing(2),
  flex: 1,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const Title = styled(Link)(({ theme }: IStyledArguments) => ({
  ...theme.typography.h6,
  marginBottom: 0,
  fontWeight: 700,
  color: theme.palette.primary.main,
}));

const Summary = styled("div")(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.95rem",
}));

interface IPreSeasonCardProps {
  id: string;
  team: any;
  currentUser: any;
}

const PreSeasonCard = injectIntl<IPreSeasonCardProps>(
  ({ id, intl }) => {
    const theme = useTheme<Theme>();
    const { data } = useQuery(TEAM_PRE_SEASON_SUMMARY, {
      variables: { teamId: id },
    });

    const exercises = (data && data.teamPreSeasonExercises) || [];

    return (
      <CardWrapper data-testid={`pre-season-summary-${id}`}>
        <Header>
          <DirectionsRunIcon style={{ color: theme.palette.primary.main }} />
          <Title to={`/teams/${id}/pre-season`}>
            {intl.formatMessage(messages.title)}
          </Title>
        </Header>
        <Content>
          <Summary>
            {exercises.length > 0
              ? `${exercises.length} ${
                  exercises.length === 1
                    ? intl.formatMessage(messages.activePlanSingular)
                    : intl.formatMessage(messages.activePlanPlural)
                }`
              : intl.formatMessage(messages.noPlans)}
          </Summary>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate(`/teams/${id}/pre-season`)}
          >
            {intl.formatMessage(messages.manage)}
          </Button>
        </Content>
      </CardWrapper>
    );
  }
);

export default PreSeasonCard;
